/**
 * Modal "Vender pilas" — Sprint 19a RP-04.
 *
 * Venta de pila(s) en UN SOLO PASO: al registrar, el backend descuenta el
 * stock (FIFO) y crea el cobro (ingreso varios por cobrar) en una operación.
 * Reemplaza los dos modales anteriores (Registrar venta + Consumir).
 *
 * Form:
 * - Casa (selector).
 * - Control de referencia (opcional): a qué control fueron las pilas. Vacío =
 *   venta suelta de repuesto. Es solo referencia, no exige control entregado.
 * - Residente (opcional — auditoría dentro de la casa).
 * - Cantidad (default 1).
 * - Descripción extra (opcional).
 *
 * El monto se muestra estimado desde el resumen de stock; el real lo calcula
 * el backend (suma de valor_venta FIFO).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { venderPilas } from '../../lib/api/pilas';
import { listRecursos } from '../../lib/api/recursos';
import {
  listarResidentesDeLote,
  type ResidenteConPersonaRead,
} from '../../lib/api/residentes';
import type {
  LoteResumen,
  RecursoRead,
  StockRecursoDisponibleResumen,
} from '../../lib/api/types';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Monto, Num } from './Monto';
import { hoy } from '../../lib/tiempo';

interface Props {
  lotes: LoteResumen[];
  stockResumen: StockRecursoDisponibleResumen[];
  onCerrar: () => void;
  onVendida: (montoTotal: number) => void;
}

export function ModalVenderPilas({
  lotes,
  stockResumen,
  onCerrar,
  onVendida,
}: Props) {
  const [loteId, setLoteId] = useState('');
  const [controlId, setControlId] = useState('');
  const [residenteId, setResidenteId] = useState('');
  const [cantidadStr, setCantidadStr] = useState('1');
  const [descripcionExtra, setDescripcionExtra] = useState('');
  const [controles, setControles] = useState<RecursoRead[]>([]);
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resumenPila = stockResumen.find((r) => r.tipo === 'pila');
  const stockDisponible = resumenPila?.cantidad_disponible ?? 0;
  const valorUnitario = resumenPila?.valor_venta_proximo ?? 0;

  const cantidad = Number.parseInt(cantidadStr || '0', 10);
  const montoEstimado = cantidad > 0 ? valorUnitario * cantidad : 0;

  // Al elegir la casa: cargar sus controles (referencia opcional) y sus
  // residentes activos (auditoría opcional).
  useEffect(() => {
    if (!loteId) {
      setControles([]);
      setControlId('');
      setResidentes([]);
      setResidenteId('');
      return;
    }
    let cancelado = false;
    listRecursos({ lote_id: loteId, tipo: 'control_remoto' })
      .then((rs) => {
        if (!cancelado) setControles(rs);
      })
      .catch(() => {
        if (!cancelado) setControles([]);
      });
    listarResidentesDeLote(loteId)
      .then((rs) => {
        if (cancelado) return;
        const fechaHoy = hoy();
        setResidentes(
          rs.filter(
            (r) =>
              r.estado === 'aprobado' &&
              (r.hasta === null || r.hasta >= fechaHoy),
          ),
        );
      })
      .catch(() => {
        if (!cancelado) setResidentes([]);
      });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  const formValido = loteId !== '' && cantidad > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await venderPilas({
        lote_id: loteId,
        cantidad,
        cr_referencia_id: controlId || null,
        residente_id: residenteId || null,
        descripcion_extra: descripcionExtra.trim() || null,
      });
      onVendida(montoEstimado);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Vender pilas"
      onClose={onCerrar}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!formValido || submitting || stockDisponible === 0}
          >
            {submitting ? 'Registrando…' : 'Vender'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm text-xs text-text-secondary">
          Al vender se <strong>descuenta el stock</strong> y se genera un
          ingreso varios <strong>por cobrar</strong> por el monto calculado.
          Cuando el vecino transfiera, clasificas el pago contra ese ingreso.
        </div>

        <div className="text-xs text-text-muted border-l-2 border-text-muted/30 pl-3">
          Stock disponible: <strong>{stockDisponible}</strong> ·{' '}
          {valorUnitario > 0 ? (
            <>
              Valor unitario: <Monto value={valorUnitario} />
            </>
          ) : (
            'sin valor de venta configurado'
          )}
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Casa</span>
          <select
            value={loteId}
            onChange={(e) => setLoteId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          >
            <option value="">— elige —</option>
            {lotes.map((l) => (
              <option key={l.id} value={l.id}>
                Casa {l.identificador}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Control de referencia (opcional)
          </span>
          <select
            value={controlId}
            onChange={(e) => setControlId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-50"
            disabled={!loteId}
          >
            <option value="">— ninguno (venta suelta) —</option>
            {controles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.identificador}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Residente (opcional — auditoría)
          </span>
          <select
            value={residenteId}
            onChange={(e) => setResidenteId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary disabled:opacity-50"
            disabled={!loteId}
          >
            <option value="">— ninguno —</option>
            {residentes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.persona_nombre_completo}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Cantidad</span>
          <input
            type="number"
            min={1}
            max={stockDisponible || 999}
            value={cantidadStr}
            onChange={(e) => setCantidadStr(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Descripción extra (opcional)
          </span>
          <input
            type="text"
            value={descripcionExtra}
            onChange={(e) => setDescripcionExtra(e.target.value)}
            placeholder="Ej: 2 de repuesto"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {cantidad > 0 && valorUnitario > 0 && (
          <div className="text-sm bg-bg-sunken border-[0.5px] border-white/[0.08] rounded px-3 py-2 text-text-primary">
            Monto estimado:{' '}
            <strong>
              <Monto value={montoEstimado} />
            </strong>{' '}
            <span className="text-text-muted text-xs">
              (<Num>{cantidad}</Num> × <Monto value={valorUnitario} />)
            </span>
          </div>
        )}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
